import { z } from "zod";

// Service types exposed by the wizard. Maps 1:1 to events.event_type.
export const PLAN_SERVICES = [
  "wedding",
  "live_service",
  "corporate",
  "drop_off",
  "venue",
] as const;
export type PlanService = (typeof PLAN_SERVICES)[number];

export function isPlanService(v: unknown): v is PlanService {
  return typeof v === "string" && (PLAN_SERVICES as readonly string[]).includes(v);
}

/** Services that use the quote form (every service except venue, #1221). */
const QUOTE_SERVICES = ["wedding", "live_service", "corporate", "drop_off"] as const;
export type QuoteService = (typeof QUOTE_SERVICES)[number];

/**
 * Phone is required on every wizard branch (#1221): every lead now gets the
 * menu link by text. 10–15 digits once formatting is stripped, so
 * "(416) 555-0199", "+1 416 555 0199" and "416-555-0199 x22" pass and
 * "555-0199" or "911" don't. Stored as typed.
 */
export const phoneSchema = z
  .string()
  .trim()
  .refine((v) => {
    const digits = v.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 15;
  }, "Please enter a phone number with area code.");

/** The quote form (wedding, live service, corporate, drop-off) — single-page progressive form. */
export const weddingQuoteSchema = z.object({
  service: z.enum(QUOTE_SERVICES),
  event_type_name: z.string().optional(),
  event_date: z.string().min(1, "Please pick a date."),
  // Optional preferences (#227): rendered behind a collapsed "Preferences"
  // section so the happy path stays short, but captured so sales can quote on
  // first contact instead of calling back for basics.
  event_time: z.string().optional(),
  // Required unless it's a drop-off pickup (checked in superRefine below).
  venue_location: z.string().optional(),
  // Drop-off only: delivery or pickup (#1221). Stored in notes.
  fulfillment: z.enum(["delivery", "pickup"]).optional(),
  guest_count: z.coerce.number().int().positive("Guest count must be greater than zero."),
  service_style: z.enum(["plated", "family_style", "buffet", "stations"]).optional(),
  bar_service: z.enum(["yes", "no"]).optional(),
  dietary: z.array(z.enum(["vegetarian", "vegan", "gluten_free", "nut_free"])).default([]),
  client_name: z.string().min(2, "Please enter your name."),
  client_email: z.string().email("Please enter a valid email."),
  // Required again (#1221, reversing #227): the menu link goes out by text.
  client_phone: phoneSchema,
  notes: z.string().max(2000).optional(),
}).superRefine((data, ctx) => {
  if (data.service === "live_service" && (!data.event_type_name || !data.event_type_name.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select an event type.",
      path: ["event_type_name"],
    });
  }
  if (data.service === "drop_off" && !data.fulfillment) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please choose delivery or pickup.",
      path: ["fulfillment"],
    });
  }
  const locationOptional = data.service === "drop_off" && data.fulfillment === "pickup";
  if (!locationOptional && !data.venue_location?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please enter a location.",
      path: ["venue_location"],
    });
  }
});
export type WeddingQuoteInput = z.infer<typeof weddingQuoteSchema>;

/**
 * Minimal fields needed to capture a "partial lead" (issue #226) — saved
 * fire-and-forget once a wizard visitor has typed a usable name + email, so the
 * lead isn't lost if they bail before submitting. Phone stays optional here,
 * because the capture fires on name/email blur, before the phone is typed.
 */
export const partialLeadSchema = z.object({
  client_name: z.string().min(2),
  client_email: z.string().email(),
  client_phone: z.string().optional(),
});
export type PartialLeadInput = z.infer<typeof partialLeadSchema>;

/** Venue inquiry — simple 5-field form. */
export const venueInquirySchema = z.object({
  event_type_note: z.enum(["wedding_reception", "corporate", "private_party", "other"]),
  event_date: z.string().min(1, "Please pick a date."),
  guest_count: z.coerce.number().int().positive(),
  client_name: z.string().min(2),
  client_email: z.string().email(),
  client_phone: phoneSchema,
});
export type VenueInquiryInput = z.infer<typeof venueInquirySchema>;
